import { useState, ChangeEvent, useEffect, FC } from "react";
import CustomTextField from "../CustomTextField/CustomTextField";
import CustomSelect from "../CustomSelect/CustomSelect";
import { Button, Container, CssBaseline, TextField, Typography } from "@mui/material";
import { styled } from "@mui/system";
import { fetchStreets, fetchTowns, fetchNormalizedAddress } from "../../Services/dataService";
import Header from "../Header/Header";
import appStyles from "./appStyle";
import ErrorMessage from "../ErrorMessage/ErrorMessage";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CustomAutocomplete from "../CustomAutocomplete/CustomAutocomplete";
import { useTranslation } from 'react-i18next';
import i18n from "../../i18n";


const FormContainer = styled("div")({
  backgroundColor: "#fff",
  borderRadius: "8px",
  boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
});

const StyledButton = styled(Button)({
  padding: "12px",
  "&:hover": {
    backgroundColor: "#e76f51",
  },
});

interface Option {
  label: string;
  value: string;
  id?: string;
  idMun?: string;
}

interface FormData {
  codigoPostal: string;
  provincia: Option | null;
  localidad: Option | null;
  via: Option | null;
  calle: Option | null;
  numero: string;
  portal: string;
  escalera: string;
  piso: string;
  puerta: string;
}

const App: FC = () => {
  const { t } = useTranslation();
  const [language, setLanguage] = useState('es');
  const [provincias, setProvincias] = useState<Option[]>([]);
  const [localidades, setLocalidades] = useState<Option[]>([]);
  const [vias, setVias] = useState<Option[]>([]);
  const [calles, setCalles] = useState<Option[]>([]);
  const [showNoMatchMessage, setShowNoMatchMessage] = useState(false);
  const [inputLocalidad, setInputLocalidad] = useState("");
  const [disableField, setDisableField] = useState(false);
  const [normalizedAddress, setNormalizedAddress] = useState({});
  const [showMessage, setShowMessage] = useState(false);
  const [inputCalle, setInputCalle] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [idLocalidad, setIdLocalidad] = useState("");
  const [allStreets, setAllStreets] = useState<Option[]>([]);

  const [formData, setFormData] = useState<FormData>({
    codigoPostal: "",
    provincia: null,
    localidad: null,
    via: null,
    calle: null,
    numero: "",
    portal: "",
    escalera: "",
    piso: "",
    puerta: "",
  });

  const [errors, setErrors] = useState({
    codigoPostal: "",
    provincia: "",
    localidad: "",
    via: "",
    calle: "",
    numero: "",
  });

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setLanguage(lng);
  };

  const validateField = (name: string, value: string) => {
    let errorMessage = "";
    let message = "";

    switch (name) {
      case "codigoPostal":
        if (!value) {
          errorMessage = t('error.required');
        }
        break;
      case "numero":
        if (!value) {
          errorMessage = t('error.required');
        } else if (!/^(S\/N|\d{1,4})$/i.test(value)) {
          errorMessage = t('error.number');
        }
        break;
      case "portal":
        if (!/^(BIS|[A-Z]|\d{1,2})$/i.test(value)) {
          message = t('error.portal');
        }
        break;
      case "escalera":
        if (!/^(INT|EXT|IZQ|DER|[A-Z]|\d{1,2})$/i.test(value)) {
          message = t('error.stairs');
        }
        break;
      case "piso":
        if (!/^(AL|AT|A[1-9]|BJ|BU|EN|ET|PR|PT|SA|SS|ST|S[1-9]|TR|\d{1,2})$/i.test(value)) {
          message = t('error.floor');
        }
        break;
      case "puerta":
        if (!/^(IZQ|DER|[A-Z]|\d{1,2})$/i.test(value)) {
          message = t('error.door');
        }
        break;
      default:
        break;
    }

    setErrorMessage(message);
    setErrors((prev) => ({ ...prev, [name]: errorMessage }));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    validateField(name, value);
  };

  const handleChange = (e: ChangeEvent<{ name?: string; value: any }>) => {
    const { name, value } = e.target as HTMLInputElement;

    if (name === 'provincia' || name === 'localidad' || name === 'via' || name === 'calle') {
      const valueForm = {
        label: value || '',
        value: value || '',
      };
      setFormData((prev) => ({ ...prev, [name]: valueForm }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    validateField(name, value);

    if (name === 'via') {
      const filteredStreets = allStreets.filter(
        (street) => street.value === value
      );
      setCalles(filteredStreets);
      setInputCalle("");
    }

  };

  useEffect(() => {
    const loadTowns = async () => {
      const codigoPostal = formData.codigoPostal;
      if (codigoPostal) {
        try {
          if (codigoPostal.length > 4) {
            const towns: any = await fetchTowns(codigoPostal);
            if (towns.length > 0) {
              setFormData((prevData) => ({
                ...prevData,
                provincia: { label: towns[0].provinceDesc, value: towns[0].provinceDesc },
              }));

              const provincias = [
                {
                  label: towns[0].provinceDesc || '',
                  value: towns[0].provinceDesc || '',
                },
              ];
              setProvincias(provincias);

              const localidades = towns.map((town: any) => {
                const townIdStr = String(town.townId);
                const idMun = townIdStr.substring(2, 5);

                return {
                  label: town.townName || '',
                  value: town.townName || '',
                  id: town.townId || '',
                  idMun: idMun || '',
                  mun: town.municipalityName || '',
                };
              });
              setLocalidades(localidades);
            }
          } else {
            setFormData({
                ...formData,
                localidad: null,
                via: null,
                calle: null,
                numero: "",
                portal: "",
                escalera: "",
                piso: "",
                puerta: "",
              });
            setLocalidades([]);
            setProvincias([]);
            setDisableField(false);
            setVias([]);
            setCalles([]);
            setAllStreets([]);
            setInputCalle("");
            setInputLocalidad("");
          }
        } catch (error) {
          console.error("Error al obtener datos:", error);
        }
      } else {
        setFormData({
          ...formData,
          localidad: null,
          via: null,
          calle: null,
          numero: "",
          portal: "",
          escalera: "",
          piso: "",
          puerta: "",
        });
        setLocalidades([]);
        setProvincias([]);
        setDisableField(false);
        setVias([]);
        setCalles([]);
        setAllStreets([]);
        setInputCalle("");
        setInputLocalidad("");
      }
    };

    loadTowns();
  }, [formData.codigoPostal]);

  useEffect(() => {
    const loadStreets = async () => {
      const codigoPostal = formData.codigoPostal;
      const localidadValue = formData.localidad;
      if (codigoPostal && localidadValue) {
        try {
          if (codigoPostal.length > 1 && localidadValue?.id !== undefined) {
            setIdLocalidad(localidadValue?.id);
            const streets: any = await fetchStreets(codigoPostal, idLocalidad);

            if (streets.length > 0) {
              const calles = streets.map((street: any) => ({
                label: street.streetDescription || '',
                value: street.streetTypeName || '',
                id: street.streetId || '',
              }));
              setAllStreets(calles);

              const uniqueVias = Array.from(
                new Set(streets.map((street: any) => street.streetTypeName || ''))
              );

              const vias = uniqueVias.map((via) => ({
                label: via as string,
                value: via as string,
              }));

              setVias(vias);

              const filteredStreets = formData.via
                ? allStreets.filter((street: any) => street.streetTypeName === formData.via?.value)
                : allStreets;

              setCalles(filteredStreets);

            }
          }
        } catch (error) {
          console.error("Error al obtener datos:", error);
        }
      } else {
        setFormData({
          ...formData,
          localidad: null,
          via: null,
          calle: null,
        });
        setLocalidades([]);
        setVias([]);
        setCalles([]);
        setAllStreets([]);
      }
    };

    loadStreets();
    setShowMessage(false);
  }, [formData.localidad]);

  const handleContinueClick = async () => {
    if (!formData.numero || !formData.calle) {
      console.error("Faltan datos obligatorios en el formulario");
      return;
    }

    const addressData = {
      idVia: String(formData.calle.id) || "",
      numero: formData.numero,
      portal: formData.portal || "",
      escalera: formData.escalera || "",
      piso: formData.piso,
      puerta: formData.puerta || "",
    };

    try {
      const normalizedAddress = await fetchNormalizedAddress(addressData);
      setNormalizedAddress(normalizedAddress);
      setShowMessage(true);

    } catch (error) {
      console.error("Error al normalizar la dirección:", error);
    }
  };

  const formatKey = (key: string) => {
    if (key === "horizontalId") return "placeID";
    if (key === "verticalId") return "masterID";
    return key.replace(/([A-Z])/g, " $1");
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => alert("Texto copiado: " + text))
      .catch(() => alert("Error al copiar el texto"));
  };

  const handleLocalidadChange = (newValue:any) => {
    if (newValue === null) {
      const valueForms = {
        label: '',
        value: '',
      };
      setFormData({ ...formData, localidad: valueForms, via: null, calle: null });
      setDisableField(true);
    } else {
      setFormData({ ...formData, localidad: newValue, via: null, calle: null });
      setDisableField(false);

    }
  };

  return (
    <>
      <CssBaseline />
      <Header />

      <Container>
        <FormContainer sx={appStyles.formContainer}>
          <Typography sx={appStyles.typographyTitle}>
            {t('welcome')}
          </Typography>
          <Typography sx={appStyles.typographyBody}>
            {t('intro')}
          </Typography>

          <Typography sx={appStyles.typographySubtitle}>{t('subtitle')}</Typography>

          <Container sx={appStyles.containerForm}>
            <CustomTextField maxWidth="200px" label={t('postalCode')} name="codigoPostal" value={formData.codigoPostal} onChange={handleChange} error={!!errors.codigoPostal} helperText={errors.codigoPostal} onBlur={handleBlur}/>

            <CustomSelect
              label={t('province')}
              name="provincia"
              value={formData.provincia ? formData.provincia.value : ''}
              onChange={handleChange}
              maxWidth="200px"
              disabled
              options={provincias}
            />

            <CustomAutocomplete
              value={formData.localidad}
              maxWidth="200px"
              disabled={localidades.length < 1}
              onChange={handleLocalidadChange}
              inputValue={inputLocalidad}
              onInputChange={setInputLocalidad}
              options={localidades}
              onShowNoMatchMessage={setShowNoMatchMessage}
              label={t('town')}
            />

            <CustomSelect
              label={t('via')}
              name="via"
              value={formData.via ? formData.via.value : ''}
              onChange={handleChange}
              maxWidth="144px"
              disabled={(!formData.localidad && !formData.via) || (disableField && !formData.via)}
              options={vias}
            />

            <CustomAutocomplete
              value={formData.calle}
              maxWidth="316px"
              disabled={!formData.via}
              onChange={(newValue) => setFormData({ ...formData, calle: newValue })}
              inputValue={inputCalle}
              onInputChange={setInputCalle}
              options={calles}
              onShowNoMatchMessage={setShowNoMatchMessage}
              label={t('street')}
            />

            <CustomTextField maxWidth="144px" disabled={!formData.calle} label={t('number')} name="numero" onChange={handleChange} value={formData.numero} error={!!errors.numero} helperText={errors.numero} onBlur={handleBlur}/>
            <CustomTextField maxWidth="200px" disabled={!formData.calle} label={t('portal')} name="portal" onChange={handleChange} value={formData.portal}/>
            <CustomTextField maxWidth="144px" disabled={!formData.calle} label={t('stairs')} name="escalera" onChange={handleChange} value={formData.escalera}/>
            <CustomTextField maxWidth="87px" disabled={!formData.calle} label={t('Piso')} name="piso" onChange={handleChange} value={formData.piso}/>
            <CustomTextField maxWidth="144px" disabled={!formData.calle} label={t('Puerta')} name="puerta" onChange={handleChange} value={formData.puerta}/>
          </Container>

          <Typography sx={appStyles.typographyBody} hidden={!formData.via} className="color">
            {t('messagePostalCode')}
          </Typography>

          {errorMessage && <ErrorMessage message={errorMessage} />}

          {showNoMatchMessage && (
            <Typography sx={appStyles.typographyMessage}>
              {t('messageF1')}
            </Typography>
          )}

          {showMessage && normalizedAddress && (
            <div style={{ backgroundColor: "rgba(0, 69, 113, 0.08)", padding: "24px", borderRadius: "4px" }}>
              <h3 style={{marginTop: "0px"}}>Dirección Normalizada:</h3>
              {Object.entries(normalizedAddress).map(([key, value]) => (
                <div key={key} style={{ marginBottom: "10px", display: "flex", alignItems: "center" }}>
                  <strong style={{ textTransform: "capitalize"}}>{formatKey(key)}:</strong>
                  <span style={{ marginLeft: "5px" }}>{String(value)}</span>
                  <button
                    onClick={() => copyToClipboard(String(value))}
                    style={{ marginLeft: "10px", background: "none", border: "none", cursor: "pointer" }}
                  >
                  <ContentCopyIcon fontSize="small" />
                  </button>
                </div>
              ))}

              {Object.entries(formData)
                  .flatMap(([key, value]) => {
                      if (key === "localidad" && value && typeof value === "object") {
                          const provinciaID = value.id ? String(value.id).slice(0, 2) : null;
                          const municipioID = value.idMun || null;
                          const municipio = value.mun || null;

                          return [
                              [key, value],
                              ["ID provincia", provinciaID],
                              ["municipio", municipio],
                              ["ID municipio", municipioID],
                          ];
                      }
                      return [[key, value]];
                  })
                  .sort(([a], [b]) => {
                      const ordenDeseado = ["codigoPostal", "provincia", "ID provincia", "localidad", "municipio", "ID municipio"];
                      const indiceA = ordenDeseado.indexOf(a);
                      const indiceB = ordenDeseado.indexOf(b);

                      if (indiceA === -1 && indiceB === -1) return 0;
                      if (indiceA === -1) return 1;
                      if (indiceB === -1) return -1;
                      return indiceA - indiceB;
                  })
                  .map(([key, value]) => {
                      let displayValue: any = value;
                      let additionalInfo: any[] = [];

                      if (typeof value === "object" && value !== null) {
                          switch (key) {
                              case "calle":
                                  displayValue = value.label || value.name || value.value || "[Calle no válida]";
                                  if (value.id) additionalInfo.push({ label: "ID Vía", value: value.id });
                                  break;
                              case "localidad":
                                  displayValue = value.label || value.name || value.value || "[Localidad no válida]";
                                  break;
                              default:
                                  displayValue = value?.value || value?.label || value || "[Dato no válido]";
                          }
                      }

                      if (!displayValue && key !== "ID provincia" && key !== "ID municipio") return null;

                      return (
                          <div key={key} style={{ marginBottom: "10px" }}>
                              <div style={{ display: "flex", alignItems: "center" }}>
                                  <strong style={{ textTransform: "capitalize"}}>
                                      {key == 'calle' ? 'Nombre Vía' : key == 'via' ? 'Tipo Vía' : key}:
                                  </strong>
                                  <span style={{ marginLeft: "5px" }}>{String(displayValue)}</span>
                                  <button
                                      onClick={() => copyToClipboard(String(displayValue))}
                                      style={{ marginLeft: "10px", background: "none", border: "none", cursor: "pointer" }}
                                  >
                                      <ContentCopyIcon fontSize="small" />
                                  </button>
                              </div>

                              {additionalInfo.map((info) => (
                                  <div key={info.label} style={{ marginTop: "5px" }}>
                                      <strong style={{ textTransform: "capitalize"}}>
                                          {info.label}:
                                      </strong>
                                      <span style={{ marginLeft: "5px" }}>{String(info.value)}</span>
                                      <button
                                          onClick={() => copyToClipboard(String(info.value))}
                                          style={{ marginLeft: "10px", background: "none", border: "none", cursor: "pointer" }}
                                      >
                                          <ContentCopyIcon fontSize="small" />
                                      </button>
                                  </div>
                              ))}
                          </div>
                      );
                  })}
                </div>
              )}

          <StyledButton onClick={handleContinueClick} sx={appStyles.styledButton} disabled={!formData.numero}>{t('continue')}</StyledButton>
        </FormContainer>
      </Container>

      <Container hidden>
        <Button onClick={() => changeLanguage('es')}>ES</Button>
        <Button onClick={() => changeLanguage('gl')}>GL</Button>
        <Button onClick={() => changeLanguage('ca')}>CA</Button>
      </Container>
    </>
  );
};

export default App;
